<template>
    <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError" />

    <!-- Overlay caricamento -->
    <div v-show="isLoading">
        <LoadingOverlay :show="isLoading" title="Caricamento in corso..." :message="loadingMessage" />
    </div>

    <div class="top-bar-consultazione">
        Dettaglio Quesito
    </div>

    <div class="card question-card mb-3">
        <div class="it-table-wrapper">
            <table class="table question-table mb-0">
                <tbody>
                    <!-- Titolo Quesito -->
                    <tr class="table-title-row">
                        <th colspan="2" class="text-center text-white">
                            Quesito del Servizio Supporto Giuridico
                        </th>
                    </tr>

                    <tr>
                        <th>Codice identificativo</th>
                        <td>{{ question.id }}</td>
                    </tr>
                    <tr>
                        <th>Data pubblicazione</th>
                        <td>{{ formatDate(question.published_on) }}</td>
                    </tr>
                    <tr>
                        <th>Argomenti</th>
                        <td>
                            <span v-for="(topic, idx) in question.topics" :key="topic.id">
                                {{ topic.title }}<span v-if="idx < question.topics.length - 1">, </span>
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>Oggetto</th>
                        <td>{{ stripHtml(question.object) }}</td>
                    </tr>
                    <tr>
                        <th>Articoli</th>
                        <td>
                            <span v-for="(art, idx) in question.articles" :key="art.id" :title="stripHtml(art.title)">
                                {{ art.title || `Art. ${art.id}` }}<span v-if="idx < question.articles.length - 1">,
                                </span>
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>Quesito</th>
                        <td>{{ stripHtml(question.text) }}</td>
                    </tr>
                    <tr>
                        <th>Risposta aggiornata</th>
                        <td>{{ stripHtml(question.opinion || '-') }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="mb-3 d-flex justify-content-between">

        <!-- Sinistra -->
        <button class="btn btn-light btn-icon" @click="$root.goTo('ListaPareriConsultabili')"
            :title="'Torna alla lista'">
            <svg class="icon">
                <use href="/bootstrap-italia/dist/svg/sprites.svg#it-arrow-left"></use>
            </svg>
        </button>

        <!-- Destra -->
        <button class="btn btn-primary btn-icon" @click="downloadPdf" :title="'Stampa PDF'">
            <svg class="icon" width="24" height="24" fill="white">
                <use href="/bootstrap-italia/dist/svg/sprites.svg#it-print"></use>
            </svg>
        </button>

    </div>
</template>

<script>
import ToastDialog from '../../../components/common/ToastDialog.vue';
import toastable from '../../../mixins/toastable';
import LoadingModal from '../../partials/LoadingModal.vue';
import LoadingOverlay from '../../partials/LoadingOverlay.vue';

export default {
    name: 'ParereView',
    mixins: [toastable],
    components: { ToastDialog, LoadingModal, LoadingOverlay },
    props: ['elementId'],
    data() {
        return {
            isLoading: true,
            question: {},
            toastTitle: '',
            toastMessage: '',
            toastError: false,
        };
    },
    methods: {
        async downloadPdf() {

            try {
                this.isLoading = true;

                const baseUrl = this.$root.config.VITE_APP_BACKOFFICE_URL.replace(/\/api$/, '');

                const res = await fetch(
                    `${baseUrl}/parere/${this.elementId}/pdf`,
                    {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/pdf'
                        }
                    }
                );

                if (!res.ok) {
                    console.error('Errore download PDF:', res.status);
                    return;
                }

                const blob = await res.blob();

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `parere_${this.elementId}.pdf`;

                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);

            } catch (error) {
                console.error('Errore durante download PDF:', error);
            }
            finally {
                this.isLoading = false;
            }
        },
        stripHtml(html) {
            if (!html) return '';
            const div = document.createElement('div');
            div.innerHTML = html;
            return div.textContent || div.innerText || '';
        },
        async loadQuestion() {
            const baseUrl = this.$root.config.VITE_APP_BACKOFFICE_URL.replace(/\/api$/, '');
            const res = await fetch(`${baseUrl}/parere/${this.elementId}`, {
                method: 'GET',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
            });

            if (!res.ok) { console.error('Errore caricamento quesito:', res.status); return; }

            const q = await res.json();
            if (!this.$root.contents) {
                await this.$root.fetchContents()
            }
            if (this.$root.contentsLoading) {
                while (this.$root.contentsLoading) {
                    await new Promise(resolve => setTimeout(resolve, 100))
                }
            }
            const allArticlesRemote = Object.values(this.$root.contents?.articles || {});
            q.articles = (q.articles || []).map(a => {
                const remote = allArticlesRemote.find(r => r.id === a.id);
                const title = remote?.versions?.length
                    ? remote.versions[remote.versions.length - 1].title
                    : null;
                return { ...a, title: title || a.title || `Art. ${a.id}` };
            });

            q.articles = (q.articles || []).map(a => ({ ...a, title: a.title || `Art. ${a.id}` }));
            this.question = q;
        },
        formatDate(dateString) {
            if (!dateString) return '—';

            const date = new Date(dateString);

            return date.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },
    },
    async mounted() {
        this.isLoading = true;
        try { await this.loadQuestion(); }
        catch (error) { console.error(error); }
        finally { this.isLoading = false; }
    }
};
</script>

<style scoped>
.question-card {
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
    padding: 1rem;
    overflow: visible;
}

.question-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    font-size: 1.125rem;
    line-height: 1.35;
}

.question-table thead {
    background-color: #0d6efd !important;
    color: white !important;
}

.question-table tbody tr:hover {
    background-color: rgba(0, 102, 204, 0.08);
    transition: background-color 0.2s;
}

.table-title-row th {
    background: linear-gradient(180deg, #0b5ed7 0%, #0066cc 100%);
    color: #ffffff;
    font-weight: 700;
    font-size: 0.95rem;
    text-align: center;
    border: none;
    padding: 0.9rem 1rem;
}

.question-table th,
.question-table td {
    border: none;
    vertical-align: top;
    padding: 0.7rem 0.9rem;
}

.question-table tbody th {
    width: 220px;
    font-weight: 700;
    color: #17324d;
    border-right: 1px solid rgba(0, 0, 0, 0.06);
    white-space: nowrap;
}

.question-table tbody td {
    color: #1f2d3d;
}

.question-table tbody tr+tr th,
.question-table tbody tr+tr td {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

@media (max-width: 768px) {
    .question-table tbody th {
        width: 160px;
        white-space: normal;
    }
}

.top-bar-consultazione {
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0) 45%),
        linear-gradient(180deg, #2f86ff 0%, #0059b3 45%, #003a7a 100%);
    color: white;
    font-size: 2rem;
    font-weight: bold;
    text-align: left;
    padding: 6rem 4rem;
    padding-left: 20rem;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}
</style>